import * as React from "react";
import * as ReactDOM from "react-dom";
import classNames from "classnames";
import { EXPANDED_MOD_MAP_LOG, OPENED_MOD_MAP_LOG } from "../../../helpers/DocumentUserLog";

import DocBookmark from "../BlockNodeHelpers/Bookmark";
import DocNote from "../BlockNodeHelpers/Note";
import DocStyledText from "../BlockNodeHelpers/StyledText";
import GenButton from "../../Gen/Button/component";
import { backgroundStyleFromNode } from "../../../helpers/Doc/changes";
import { MEDIA_PICKER } from "../../../helpers/Doc/menu";
import PlaceholderImage from "images/placeholder-image.png";
import { getHighlightStyle } from "../../../helpers/utils";

const DAM_DISTRIBUTION_ENDPOINT = "https://d3pk024wjiydi7.cloudfront.net/filestore/";

const onExpandCollapseClick =
  (toggleImageModal: () => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toggleImageModal();
  };

interface DocImageProps extends DocImageType<InlineNode>, BlockNodeAll {
  inGallery?: boolean;
}

interface DocImageState {
  imageModalOn: boolean;
}

export default class DocImage extends React.PureComponent<DocImageProps, DocImageState> {
  compressButtonRef: React.RefObject<HTMLButtonElement>;

  get isDelete() {
    return this.props.blockChangeHighlight?.type === "DELETE_CONTENT";
  }

  constructor(props: DocImageProps) {
    super(props);

    this.compressButtonRef = React.createRef();

    this.state = {
      imageModalOn: false,
    };
  }

  isDeleteGuard(e?: React.MouseEvent<HTMLElement>) {
    if (this.isDelete) {
      this.preventInteraction(e);
      return true;
    }
    return false;
  }

  componentDidUpdate(_prevProps: DocImageProps, prevState: DocImageState) {
    if (!prevState.imageModalOn && this.state.imageModalOn) {
      // Modal just opened
      if (this.compressButtonRef.current) {
        this.compressButtonRef.current.focus();
        this.compressButtonRef.current.classList.add("current-element");
      }
      document.addEventListener("keydown", this.handleModalKeyDown);
    } else if (prevState.imageModalOn && !this.state.imageModalOn) {
      // Modal just closed
      document.removeEventListener("keydown", this.handleModalKeyDown);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleModalKeyDown);
  }

  handleModalKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.state.imageModalOn) {
      this.setState({ imageModalOn: false });
    }
  };

  openPicker(e: React.MouseEvent<HTMLAnchorElement>) {
    if (this.isDeleteGuard(e)) return;

    const { editingContext } = this.props;

    if (editingContext.isEditing) {
      editingContext.setCurrentMenu(MEDIA_PICKER, { mediaType: "image", uid: this.props.uid });
    }

    e.stopPropagation();
  }

  toggleImageModal(e?: React.MouseEvent<HTMLElement>) {
    if (this.isDeleteGuard(e)) return;

    if (!this.state.imageModalOn && this.props.classes?.includes("mod-map-image")) {
      this.props.userContext.logEvent(EXPANDED_MOD_MAP_LOG);
    }
    this.setState({ imageModalOn: !this.state.imageModalOn });
  }

  onImageExternalLinkClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (this.isDeleteGuard(e)) return;

    e.stopPropagation();
    if (this.props.classes?.includes("mod-map-image")) {
      this.props.userContext.logEvent(OPENED_MOD_MAP_LOG);
    }
    return true;
  }

  getImageURLAndExternalLink() {
    const { url, uri, mediaContext, documentContext, currentVersion } = this.props;
    let imageURL: string;
    let imageExternalLink: string;

    if (url) {
      // Try to find the asset matching the URL
      // Note mediaContext.images will be empty if this is an embedded context
      const asset: Partial<DigitalAsset> =
        mediaContext.images.find(
          (i) =>
            i.distribution_url === url ||
            url.includes(i.asset_path) ||
            url.includes(i.asset_path.replace("/filestore/", "")),
        ) || {};

      // Use the URL if it has a domain, otherwise fallback to asset.distribution_url,
      // and if there is no asset, fallback to DAM_DISTRIBUTION_ENDPOINT + url
      // NOTE: This is a workaround, not a good permanent solution.
      // In production, some images come with incomplete URLs missing the domain.
      // So if the URL doesn't have a domain, we fallback to distribution_url.
      // Ideally, URLs should always be complete before reaching this point.
      // This may be due to an issue with publishing ReusableObjects with images.
      const hasDomain = url.startsWith("http://") || url.startsWith("https://");
      imageURL = hasDomain ? url : asset.distribution_url;

      // If there is no asset found, we still want to construct a working url
      imageURL = imageURL || `${DAM_DISTRIBUTION_ENDPOINT}${url}`;

      // Derive external_id from asset or fallback to parsing the URL
      const external_id = asset.external_identifier || url.split("/").pop()?.split("_")[0];

      imageExternalLink = `/documents/${documentContext.id}/asset_pdf.pdf?external_id=${encodeURIComponent(external_id || "")}`;
    } else if (uri && uri !== "blank.jpg") {
      Rollbar.warning("Deprecated render DocImage with uri called");
      imageURL = currentVersion.localImageURL(uri);
      imageExternalLink = `/documents/${documentContext.id}/image_pdf.pdf?image_name=${encodeURIComponent(uri)}`;
    } else {
      imageURL = PlaceholderImage;
      imageExternalLink = "#";
    }

    return { imageURL, imageExternalLink };
  }

  renderModalContent(imageURL: string) {
    const altText = this.getAltText();
    return (
      <div className="image-modal-outer" onClick={this.toggleImageModal.bind(this)}>
        <div className="image-modal-inner doc-image">
          <img src={imageURL} alt={altText} />
          <button
            type="button"
            aria-label="Close larger version"
            ref={this.compressButtonRef}
            onClick={onExpandCollapseClick(this.toggleImageModal.bind(this))}
            className="doc-image-compress-button"
          >
            <i className="fa fa-compress" aria-hidden="true"> </i>
          </button>
        </div>
      </div>
    );
  }

  preventInteraction(e?: React.MouseEvent<HTMLElement>) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  render() {
    const { imageURL, imageExternalLink } = this.getImageURLAndExternalLink();
    const altText = this.getAltText();

    const notAllowedCursorStyle = this.isDelete ? { cursor: "not-allowed" } : undefined;

    const image = (
      <figure
        id={this.props.uid}
        className={classNames("doc-image", this.props.uid)}
        contentEditable={false}
        style={getHighlightStyle({
          blockChangeHighlight: this.props.blockChangeHighlight,
          editingContext: this.props.editingContext,
        })}
      >
        <div
          className="doc-image-wrapper"
          onClick={this.toggleImageModal.bind(this)}
          style={{ position: "relative", ...notAllowedCursorStyle }}
        >
          <img style={backgroundStyleFromNode(this.props)} src={imageURL} alt={altText} />
          {this.isDelete && <DeletedImageOverlay />}
          {imageExternalLink !== "#" && (
            <a
              href={imageExternalLink}
              onClick={this.onImageExternalLinkClick.bind(this)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in new tab"
              className="doc-image-external-link-button"
              style={{ ...notAllowedCursorStyle }}
            >
              <i className="fa fa-external-link-alt" aria-hidden="true"> </i>
            </a>
          )}
          <button
            type="button"
            aria-label="Larger version"
            onClick={onExpandCollapseClick(this.toggleImageModal.bind(this))}
            className="doc-image-expand-button"
            style={{ ...notAllowedCursorStyle }}
          >
            <i className="fa fa-expand" aria-hidden="true"></i>
          </button>
          {this.props.editingContext.isEditing &&
            this.props.userContext.permissions.viewMediaAssets && (
              <GenButton
                key="picker-button"
                className="doc-image-picker-button"
                name={<i className="fa fa-image"></i>}
                highlighted
                small
                onClick={this.openPicker.bind(this)}
                style={{ ...notAllowedCursorStyle }}
                linkStyle={{ ...notAllowedCursorStyle }}
              />
            )}
        </div>
        <figcaption>
          <DocStyledText
            uid={this.props.uid}
            content={this.props.title || ""}
            contentKey={"title"}
            editingContext={this.props.editingContext}
            placeholder={"Enter Image caption here"}
            independentlyEditable
            disableStyleTextEditing={this.props.disableStyleTextEditing}
          />
        </figcaption>
        {this.state.imageModalOn &&
          ReactDOM.createPortal(this.renderModalContent(imageURL), document.body)}
      </figure>
    );

    if (this.props.inGallery) {
      return image;
    } else {
      return (
        <>
          <span></span>
          <div
            data-is-node
            data-uid={this.props.uid}
            className={classNames("doc-image-single", this.props.uid, {
              [`doc-image-alignment-${this.props.alignment}`]: this.props.alignment,
              "focused-bookmark": this.props.isFocusedBookmark && !this.props.simple,
            })}
            contentEditable={false}
            style={{ ...backgroundStyleFromNode(this.props) }}
          >
            {image}
            {!this.props.simple && <DocNote {...this.props} />}
            {!this.props.simple && <DocBookmark {...this.props} />}
          </div>
          <span></span>
        </>
      );
    }
  }

  getAltText() {
    if (typeof this.props.alt_text === "string") {
      const trimmedAlt = this.props.alt_text.trim();
      if (trimmedAlt.length > 0) {
        return trimmedAlt;
      }
    }

    if (typeof this.props.title === "string" && this.props.title.trim()) {
      return this.props.title;
    }

    return "Document image";
  }
}

const DeletedImageOverlay: React.FC = () => {
  return (
    <div
      className="doc-image-delete-cross"
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      <svg width="60%" height="60%" viewBox="0 0 100 100">
        <title>Deleted image overlay</title>
        <line x1="10" y1="10" x2="90" y2="90" stroke="red" strokeWidth="5" strokeLinecap="round" />
        <line x1="90" y1="10" x2="10" y2="90" stroke="red" strokeWidth="5" strokeLinecap="round" />
      </svg>
    </div>
  );
};
